/**
 * ValidacionesCampos.js
 * 
 * Archivo centralizado de funciones de validación para formularios.
 * Todas las funciones retornan true si el campo es válido o está vacío,
 * y false si el formato es inválido.
 * 
 * Uso:
 * import { esEmailValido, esRNCValido } from '../util/ValidacionesCampos';
 */

/**
 * Valida formato de correo electrónico
 * @param {string} email - Email a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esEmailValido = (email) => {
  if (!email || email.trim() === '') return true;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Valida RNC (Registro Nacional del Contribuyente - República Dominicana)
 * - 9 dígitos: RNC empresarial
 * - 11 dígitos: Cédula usada como RNC (personas físicas con negocio)
 * @param {string} rnc - RNC a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esRNCValido = (rnc) => {
  if (!rnc || rnc.trim() === '') return true;
  const rncLimpio = rnc.replace(/\D/g, '');
  // RNC: 9 dígitos (empresas) o 11 dígitos (cédula como RNC)
  return rncLimpio.length === 9 || rncLimpio.length === 11;
};

/**
 * Valida Cédula de identidad (República Dominicana)
 * Debe tener 11 dígitos (con o sin guiones)
 * @param {string} cedula - Cédula a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esCedulaValida = (cedula) => {
  if (!cedula || cedula.trim() === '') return true;
  // Eliminar guiones y espacios
  const soloDigitos = cedula.replace(/[-\s]/g, '');
  // Validar que tenga exactamente 11 dígitos
  const regex = /^\d{11}$/;
  return regex.test(soloDigitos);
};

/**
 * Valida número de teléfono
 * Acepta varios formatos: (809) 123-4567, 809-123-4567, 8091234567, +1 809 123 4567
 * Debe tener entre 10 y 15 dígitos
 * @param {string} telefono - Teléfono a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esTelefonoValido = (telefono) => {
  if (!telefono || telefono.trim() === '') return true;
  // Eliminar todo excepto dígitos para contar
  const soloDigitos = telefono.replace(/[^\d]/g, '');
  // Debe tener entre 10 y 15 dígitos
  if (soloDigitos.length < 10 || soloDigitos.length > 15) return false;
  // Validar que solo contenga caracteres válidos (dígitos, espacios, paréntesis, guiones, +)
  const regex = /^[\d\s()-+]+$/;
  return regex.test(telefono);
};


/**
 * Valida número de pasaporte
 * Acepta de 6 a 14 caracteres alfanuméricos
 * @param {string} pasaporte - Pasaporte a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esPasaporteValido = (pasaporte) => {
  if (!pasaporte || pasaporte.trim() === '') return true;
  const regex = /^[A-Z0-9]{6,14}$/i;
  return regex.test(pasaporte);
};

/**
 * Valida URL
 * @param {string} url - URL a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esURLValida = (url) => {
  if (!url || url.trim() === '') return true;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Valida que un número esté dentro de un rango
 * @param {number} valor - Valor a validar
 * @param {number} min - Valor mínimo permitido
 * @param {number} max - Valor máximo permitido
 * @returns {boolean} - true si está en el rango o está vacío
 */
export const estaEnRango = (valor, min, max) => {
  if (valor === null || valor === undefined || valor === '') return true;
  const num = parseFloat(valor);
  return num >= min && num <= max;
};

/**
 * Valida longitud de texto
 * @param {string} texto - Texto a validar
 * @param {number} min - Longitud mínima
 * @param {number} max - Longitud máxima
 * @returns {boolean} - true si la longitud es válida o está vacío
 */
export const longitudValida = (texto, min, max) => {
  if (!texto || texto.trim() === '') return true;
  const length = texto.length;
  return length >= min && length <= max;
};

/**
 * Valida que solo contenga números
 * @param {string} valor - Valor a validar
 * @returns {boolean} - true si solo contiene números o está vacío
 */
export const esSoloNumeros = (valor) => {
  if (!valor || valor.trim() === '') return true;
  const regex = /^\d+$/;
  return regex.test(valor);
};

/**
 * Valida que solo contenga letras (con espacios y acentos)
 * @param {string} valor - Valor a validar
 * @returns {boolean} - true si solo contiene letras o está vacío
 */
export const esSoloLetras = (valor) => {
  if (!valor || valor.trim() === '') return true;
  const regex = /^[a-záéíóúñüA-ZÁÉÍÓÚÑÜ\s]+$/;
  return regex.test(valor);
};

/**
 * Valida fecha de nacimiento (mayor de edad - 18 años)
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {boolean} - true si es mayor de 18 años o está vacío
 */
export const esMayorDeEdad = (fecha) => {
  if (!fecha || fecha.trim() === '') return true;
  const hoy = new Date();
  const fechaNac = new Date(fecha);
  const edad = Math.floor((hoy - fechaNac) / (365.25 * 24 * 60 * 60 * 1000));
  return edad >= 18;
};

/**
 * Valida código postal (5 dígitos)
 * @param {string} codigoPostal - Código postal a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esCodigoPostalValido = (codigoPostal) => {
  if (!codigoPostal || codigoPostal.trim() === '') return true;
  const regex = /^\d{5}$/;
  return regex.test(codigoPostal);
};

/**
 * Valida contraseña fuerte
 * Debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial
 * @param {string} password - Contraseña a validar
 * @returns {boolean} - true si es válida o está vacío
 */
export const esPasswordFuerte = (password) => {
  if (!password || password.trim() === '') return true;
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return regex.test(password);
};

/**
 * Valida número de cuenta bancaria (mínimo 10 dígitos)
 * @param {string} cuenta - Número de cuenta a validar
 * @returns {boolean} - true si es válido o está vacío
 */
export const esCuentaBancariaValida = (cuenta) => {
  if (!cuenta || cuenta.trim() === '') return true;
  const limpiarCuenta = cuenta.replace(/[-\s]/g, '');
  const regex = /^\d{10,}$/;
  return regex.test(limpiarCuenta);
};

/**
 * Valida porcentaje (0-100)
 * @param {number} porcentaje - Porcentaje a validar
 * @returns {boolean} - true si está entre 0 y 100 o está vacío
 */
export const esPorcentajeValido = (porcentaje) => {
  return estaEnRango(porcentaje, 0, 100);
};

/**
 * Valida que una fecha no sea futura
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {boolean} - true si no es futura o está vacío
 */
export const noEsFechaFutura = (fecha) => {
  if (!fecha || fecha.trim() === '') return true;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fechaIngresada = new Date(fecha);
  fechaIngresada.setHours(0, 0, 0, 0);
  return fechaIngresada <= hoy;
};

/**
 * Valida que una fecha y hora no sea futura
 * @param {string} fechaHora - Fecha y hora en formato YYYY-MM-DDTHH:mm
 * @returns {boolean} - true si no es futura o está vacío
 */
export const noEsFechaHoraFutura = (fechaHora) => {
  if (!fechaHora || fechaHora.trim() === '') return true;
  const ahora = new Date();
  const fechaIngresada = new Date(fechaHora);
  return fechaIngresada <= ahora;
};

/**
 * Valida que fecha2 sea posterior a fecha1
 * @param {string} fecha1 - Primera fecha en formato YYYY-MM-DD
 * @param {string} fecha2 - Segunda fecha en formato YYYY-MM-DD
 * @returns {boolean} - true si fecha2 es posterior o está vacío
 */
export const esFechaPosterior = (fecha1, fecha2) => {
  if (!fecha1 || !fecha2 || fecha1.trim() === '' || fecha2.trim() === '') return true;
  const date1 = new Date(fecha1);
  const date2 = new Date(fecha2);
  return date2 > date1;
};

/**
 * Valida que un número sea >= 0
 * @param {number} valor - Valor a validar
 * @returns {boolean} - true si es >= 0 o está vacío
 */
export const esNumeroNoNegativo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return true;
  const num = parseFloat(valor);
  return !isNaN(num) && num >= 0;
};

/**
 * Valida que un número sea > 0
 * @param {number} valor - Valor a validar
 * @returns {boolean} - true si es > 0 o está vacío
 */
export const esNumeroMayorQueCero = (valor) => {
  if (valor === null || valor === undefined || valor === '') return true;
  const num = parseFloat(valor);
  return !isNaN(num) && num > 0;
};

// Exportación por defecto con todas las funciones
export default {
  esEmailValido,
  esRNCValido,
  esCedulaValida,
  esTelefonoValido,
  esPasaporteValido,
  esURLValida,
  estaEnRango,
  longitudValida,
  esSoloNumeros,
  esSoloLetras,
  esMayorDeEdad,
  esCodigoPostalValido,
  esPasswordFuerte,
  esCuentaBancariaValida,
  esPorcentajeValido,
  noEsFechaFutura,
  noEsFechaHoraFutura,
  esFechaPosterior,
  esNumeroNoNegativo,
  esNumeroMayorQueCero
};
